import React, { useRef, useState, useEffect } from "react";

// third party libaries
import Barcode from "react-barcode";
import { Row, Col, Alert, Spinner, Button } from "react-bootstrap";
import QRCode from "qrcode.react";
import _ from "lodash";
import moment from "moment";
import { toJpeg } from "html-to-image";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FaArrowRight, FaInfoCircle } from "react-icons/fa";

// constants
import logoLogin from "../../image/logo_main.png";
import {
  checkLogoLogictics,
  convertLogicticsName,
  imageRender,
} from "../../helpers";
import { COLOR_WHITE, LINEAR_GRADIENT_BG } from "../../constants";
import { MdOutlineFullscreen, MdOutlineFullscreenExit } from "react-icons/md";

/**
 * html-to-image REJECTS the whole capture if any single <img> fails to load.
 *
 * Verified in headless Chrome against a copy of this bill: with one unreachable logo in
 * the DOM, toJpeg threw `[object Event]` (the image's error event) after ~150ms and
 * produced nothing. Remove that image and it returns a valid 17KB JPEG.
 *
 * checkLogoLogictics() returns REMOTE urls we do not control — including gstatic.com
 * thumbnail links, which rotate and expire — so a logo the shop has no control over can
 * silently cost the customer their bill image, and the download button its file.
 *
 * A 1x1 transparent GIF as imagePlaceholder makes a failed image degrade to a missing
 * logo instead of a failed capture.
 */
const CAPTURE_OPTS = {
  quality: 0.95,
  imagePlaceholder:
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
};

export default function BillDegitalLogicstics({
  dataTracking,
  loadingOrderDelivery,
  onImageReady,
}) {
  const billRef = useRef();
  const emittedFor = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // download digital bill
  /**
   * Hand the rendered label to the caller once, as a JPEG data URL.
   *
   * Same toJpeg call the download button uses, so the customer is sent exactly the image
   * they can see. Opt-in via the callback, so every other usage is untouched, and guarded
   * on the tracking number so a re-render cannot re-upload and re-send the same bill.
   */
  useEffect(() => {
    const trackingNo = dataTracking?.deliveryTackingNo;
    if (!onImageReady || !trackingNo || loadingOrderDelivery) return;
    if (emittedFor.current === trackingNo) return;
    if (!billRef.current) return;

    let cancelled = false;
    // One frame of slack so barcode and QR children have painted.
    const id = setTimeout(async () => {
      try {
        const image = await toJpeg(billRef.current, CAPTURE_OPTS);
        if (cancelled) return;
        emittedFor.current = trackingNo;
        onImageReady(image, trackingNo);
      } catch (error) {
        // Never surface this to the customer — the bill on screen is unaffected.
        console.error("bill image capture failed:", error);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [dataTracking?.deliveryTackingNo, loadingOrderDelivery, onImageReady]);

  const downloadBillAsJpg = async () => {
    try {
      if (billRef.current) {
        const image = await toJpeg(billRef.current, CAPTURE_OPTS); // Convert to JPG
        const link = document.createElement("a");
        link.href = image;
        link.download = `ບິນຮັບເຄື່ອງ ${convertLogicticsName(dataTracking?.delivery)}.jpg`;
        document.body.appendChild(link); // Attach to DOM

        if (typeof link.click === "function") {
          link.click(); // Trigger download
        } else {
          window.open(link.href, "_blank"); // Open in a new tab
        }

        document.body.removeChild(link); // Clean up
      }
    } catch (error) {
      console.error("Failed to download bill as JPG:", error);
    }
  };

  if (loadingOrderDelivery) {
    return (
      <div className="card-degital-bill">
        <div
          style={{
            width: "100%",
            padding: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner size="lg" variant="success" />
          <small>ກຳລັງໂຫລດຂໍ້ມູນ</small>
        </div>
      </div>
    );
  }

  if (_.isEmpty(dataTracking?.deliveryTackingNo)) {
    return (
      <div className="card-degital-bill">
        <Alert variant="warning">
          <Alert.Heading className="blinking-note">
            <FaInfoCircle style={{ fontSize: 34, color: "orange" }} /> ໝາຍເຫດ
          </Alert.Heading>
          <p>
            ຂໍ້ມູນການຂົນສົ່ງຈະສະແດງຜົນໃນຫນ້ານີ້ ຫຼັງຈາກທີ່ລູກຄ້າ ຊຳລະເງິນ ແລະ
            ທາງຮ້ານຢືນຢັນຈັດສົ່ງຮຽບຮ້ອຍ
          </p>
          <style
            dangerouslySetInnerHTML={{
              __html: `
                        @keyframes blinking {
                        0% { opacity: 1; }
                        50% { opacity: 0.3; }
                        100% { opacity: 1; }
                        }
                        .blinking-note {
                        animation: blinking 1.5s infinite;
                        }
                    `,
            }}
          />
        </Alert>
      </div>
    );
  }

  const handleFullScreen = () => {
    const element = billRef.current; // Get the element you want to make fullscreen

    if (element) {
      if (document.fullscreenElement) {
        // Exit fullscreen
        setIsFullScreen(false);
        document.exitFullscreen();
      } else {
        // Enter fullscreen
        setIsFullScreen(true);
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.mozRequestFullScreen) {
          // Firefox
          element.mozRequestFullScreen();
        } else if (element.webkitRequestFullscreen) {
          // Chrome, Safari and Opera
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          // IE/Edge
          element.msRequestFullscreen();
        }
      }
    }
  };

  return (
    <React.Fragment>
      <div className="card-degital-bill" ref={billRef}>
        <div className="card-db-header">
          <div style={{ display: "flex", gap: 15 }}>
            <div className="d-flex justify-content-center flex-column align-items-center">
              <img
                src={checkLogoLogictics(dataTracking?.delivery)}
                alt="logicstic-logo"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  border: "1px solid #ddd",
                }}
              />
              <p style={{ fontWeight: "bold" }}>
                {convertLogicticsName(dataTracking?.delivery)}
              </p>
            </div>
            <div className="d-flex justify-content-center flex-column align-items-center">
              <img
                src={logoLogin}
                className="brand-logo-header"
                alt="brand-logo"
                style={{
                  width: 44,
                  border: "1px solid #ddd",
                  borderRadius: 10,
                  height: 44,
                }}
              />
              <p style={{ fontWeight: "bold" }}>Super CF</p>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "end",
              flexDirection: "column",
            }}
          >
            <p>ວັນທີ,ເດືອນ,ປີ ເວລາ</p>
            <p style={{ marginTop: "-1em" }}>
              {moment(dataTracking?.createdAt).format("DD/MM/YYYY HH:mm a")}
            </p>
          </div>
        </div>

        <div className="card-barcode-degital">
          <Barcode
            value={dataTracking?.deliveryTackingNo}
            height={80}
            width={2}
            fontSize={20}
          />
        </div>
        <Row>
          <Col>
            <p>ຜູ້ຝາກ: {dataTracking?.sender?.fullName}</p>
            <p>
              ເບີໂທ: <u>{dataTracking?.sender?.tel}</u>{" "}
            </p>
          </Col>
          <Col style={{ borderLeft: "1px solid #dedede" }}>
            <p>ຜູ້ຮັບ: {dataTracking?.receiver?.fullName} </p>
            <p>
              ເບີໂທ: <u>{dataTracking?.receiver?.tel}</u>
            </p>
          </Col>
        </Row>
        <div
          style={{
            textAlign: "center",
            borderBottom: "1px solid #dedede",
            borderTop: "1px solid #dedede",
            padding: 4,
          }}
        >
          <h5 style={{ fontWeight: "bold" }}>
            {dataTracking?.sender?.branch} <FaArrowRight />{" "}
            {dataTracking?.receiver?.branch}
          </h5>
        </div>

        <div
          style={{
            padding: 10,
            width: "100%",
            display: "flex",
            flexDirection: isFullScreen ? "column" : "row",
            justifyContent: isFullScreen ? "center" : "space-between",
            alignItems: "center",
          }}
        >
          <QRCode
            value={dataTracking?.deliveryTackingNo}
            style={{
              width: isFullScreen ? "60%" : "28%",
              height: isFullScreen ? "60%" : "28%",
            }}
          />
          <div
            className={`d-flex justify-content-center align-items-center gap-2 ${isFullScreen ? "flex-row mt-4" : "flex-column"}`}
          >
            <Button
              variant="light"
              style={{ width: isFullScreen ? 160 : 130 }}
              onClick={handleFullScreen}
              className="download-digital-bill"
            >
              {isFullScreen ? (
                <>
                  <MdOutlineFullscreenExit /> ສະແດງໜ້ອຍລົງ{" "}
                </>
              ) : (
                <>
                  {" "}
                  <MdOutlineFullscreen /> ເບິ່ງເຕັມຈໍ{" "}
                </>
              )}
            </Button>
            <Button
              variant="light"
              style={{
                background: LINEAR_GRADIENT_BG,
                color: COLOR_WHITE,
                width: isFullScreen ? 160 : 130,
              }}
              onClick={downloadBillAsJpg}
              className="download-digital-bill"
            >
              <FontAwesomeIcon icon={faDownload} /> ດາວໂຫລດບິນ
            </Button>
          </div>
        </div>
        <div
          style={{
            borderTop: "1px solid #dedede",
            paddingTop: 5,
            textAlign: "center",
          }}
        >
          <small style={{ color: "#333", opacity: 0.5 }}>
            ໝາຍເຫດ: ຖ້າລູກຄ້າຜູ້ໃດມີ Digital Bill ນີ້
            ສາມາດຮັບເຄື່ອງນຳຂົນສົ່ງຕາມບິນໄດ້ເລີຍ "ຂອບໃຈ ທີ່ໃຊ້ບໍລິການຂອງ ໂຟບີ"
          </small>
        </div>
      </div>
    </React.Fragment>
  );
}
